import {
  Button,
  makeStyles,
  TableBody,
  TableCell,
  TableFooter,
  TableRow
} from "@material-ui/core";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { translateBoolean } from "@saleor/intl";
import { renderCollection } from "@saleor/misc";
import { RotaryList_rotarys_edges_node } from "@saleor/rotary/types/RotaryList";
import { RotaryListUrlSortField } from "@saleor/rotary/urls";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import { RotaryTypeEnum } from "@saleor/types/globalTypes";
import { getArrowDirection } from "@saleor/utils/sort";
import moment from "moment-timezone";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

export interface RotaryListProps
  extends ListProps,
    ListActions,
    SortPage<RotaryListUrlSortField> {
  rotary: RotaryList_rotarys_edges_node[];
  onApply: (rotary: any) => any;
  onCopy: (rotary: any) => any;
}

const numberOfColumns = 8;
const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colName: {
        width: "auto"
      }
    }
  }),
  { name: "RotaryList" }
);

const RotaryList: React.FC<RotaryListProps> = ({
  rotary,
  disabled,
  isChecked,
  onNextPage,
  onPreviousPage,
  onRowClick,
  pageInfo,
  selected,
  sort,
  toggle,
  toggleAll,
  toolbar,
  onSort,
  onApply,
  onCopy
}) => {
  const classes = useStyles({});
  const intl = useIntl();

  return (
    <ResponsiveTable>
      <TableHead
        colSpan={numberOfColumns}
        selected={selected}
        disabled={disabled}
        items={rotary}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader
          className={classes.colName}
          direction={
            sort.sort === RotaryListUrlSortField.name
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(RotaryListUrlSortField.name)}
        >
          <FormattedMessage defaultMessage="Theme" />
        </TableCellHeader>
        <TableCellHeader
          className={classes.colName}
          direction={
            sort.sort === RotaryListUrlSortField.number
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(RotaryListUrlSortField.number)}
        >
          <FormattedMessage defaultMessage="Number" />
        </TableCellHeader>
        <TableCellHeader
          className={classes.colName}
          direction={
            sort.sort === RotaryListUrlSortField.isEnable
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(RotaryListUrlSortField.isEnable)}
        >
          <FormattedMessage defaultMessage="IsEnable" />
        </TableCellHeader>
        <TableCellHeader
          className={classes.colName}
          direction={
            sort.sort === RotaryListUrlSortField.createDate
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(RotaryListUrlSortField.createDate)}
        >
          <FormattedMessage defaultMessage="CreateDate" />
        </TableCellHeader>

        <TableCellHeader
          className={classes.colName}
          direction={
            sort.sort === RotaryListUrlSortField.startDate
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(RotaryListUrlSortField.startDate)}
        >
          <FormattedMessage defaultMessage="startDate" />
        </TableCellHeader>

        <TableCellHeader
          className={classes.colName}
          direction={
            sort.sort === RotaryListUrlSortField.endDate
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(RotaryListUrlSortField.endDate)}
        >
          <FormattedMessage defaultMessage="endDate" />
        </TableCellHeader>

        <TableCellHeader>
          <FormattedMessage defaultMessage="Apply Button" />
        </TableCellHeader>
        <TableCellHeader>
          <FormattedMessage defaultMessage=" " />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          rotary,
          arotary => {
            const isSelected = arotary ? isChecked(arotary.id) : false;

            return (
              <TableRow
                selected={isSelected}
                hover={!!arotary}
                key={arotary ? arotary.id : "skeleton"}
                onClick={arotary && onRowClick(arotary.id)}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(arotary.id)}
                  />
                </TableCell>
                <TableCell className={classes.colName}>
                  {arotary ? arotary.theme : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colName}>
                  {arotary ? arotary.number : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colName}>
                  {arotary ? (
                    translateBoolean(arotary.isEnable, intl)
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colName}>
                  {arotary ? (
                    moment(arotary.createDate).format("YYYY-MM-DD HH:mm:ss")
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colName}>
                  {arotary ? (
                    arotary.startDate ? (
                      moment(arotary.startDate).format("YYYY-MM-DD HH:mm:ss")
                    ) : (
                      ""
                    )
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colName}>
                  {arotary ? (
                    arotary.endDate ? (
                      moment(arotary.endDate).format("YYYY-MM-DD HH:mm:ss")
                    ) : (
                      ""
                    )
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colName}>
                  {arotary ? (
                    !!!arotary.isEnable &&
                    arotary.rotaryType !== RotaryTypeEnum.BANNER_BOX ? (
                      <Button
                        className={classes.apply}
                        onClick={onApply(arotary)}
                        color={"primary"}
                      >
                        Apply
                      </Button>
                    ) : (
                      ""
                    )
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>

                <TableCell className={classes.colName}>
                  <Button
                    className={classes.apply}
                    onClick={onCopy(arotary)}
                    color={"primary"}
                  >
                    copy
                  </Button>
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No rotary found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};

export default RotaryList;
